// Shared inventory + brand-background cache and idle-time image preloader.
//
// Two consumers read from the cache:
//   1. BrandCarousel (the /brand page) — calls fetchItems / fetchBrandBackgrounds
//      directly when it mounts. Hits the TTL cache instantly when warm.
//   2. warmupAll(), kicked off from HomePage on landing-page idle, does the
//      same fetches plus trickle-loads product images into the browser HTTP
//      cache so cards paint instantly when the user navigates. Not the
//      images on a touchscreen, where they cost a phone's data.
//
// In-flight dedupe ensures concurrent calls share the same network request
// (e.g. user clicks BRANDS while warmup is mid-fetch — single API call).

import { dummyFetch } from "./dummyListings.js";
import { isTouchDevice } from "./viewport.js";

const TTL_MS = 5 * 60 * 1000;
// A failed brand-backgrounds request is kept this long, not the full TTL: kept
// five minutes, one bad answer on a cold load left SHOWCASE's tiles and the
// showcase itself without backdrops until then.
const FAILED_RETRY_MS = 15 * 1000;
const failedStamp = () => Date.now() - TTL_MS + FAILED_RETRY_MS;

// Throttle: ~25 images/s. Two parallel fetches per tick stays well under
// the browser's per-domain concurrent-connection limit (~6) and gives us
// enough headroom in a ~5s landing-page window to warm 100+ images.
const IMAGE_PRELOAD_INTERVAL_MS = 80;
const IMAGES_PER_TICK = 2;
const MAX_IMAGES_TO_PRELOAD = 140;
// Tiny gap before the first image fetch so the landing page's first paint
// isn't competing with image requests.
const WARMUP_KICKOFF_DELAY_MS = 100;

let _itemsCache = { items: null, ts: 0 };
let _soldCache = { items: null, ts: 0 };
// `hidden`: the brands the owner has left out of the Showcase (site manager's
// per-brand switch), lowercased, read from the same response as the backdrops.
let _bgsCache = { map: null, hidden: null, ts: 0 };
let _itemsInFlight = null;
let _soldInFlight = null;
let _bgsInFlight = null;
let _warmupAborted = false;
let _warmupTimer = null;

function isFresh(entry) {
  return entry.ts > 0 && Date.now() - entry.ts < TTL_MS;
}

export async function fetchItems() {
  if (_itemsCache.items && isFresh(_itemsCache)) return _itemsCache.items;
  if (_itemsInFlight) return _itemsInFlight;
  _itemsInFlight = (async () => {
    try {
      const res = await dummyFetch("/api/inventory/?show_sold=false&limit=200", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Failed to load inventory");
      const json = await res.json();
      const raw = Array.isArray(json) ? json : json.items || [];
      const live = raw.filter((item) => item.state === "LIVE");
      _itemsCache = { items: live, ts: Date.now() };
      return live;
    } finally {
      _itemsInFlight = null;
    }
  })();
  return _itemsInFlight;
}

export async function fetchSoldItems() {
  if (_soldCache.items && isFresh(_soldCache)) return _soldCache.items;
  if (_soldInFlight) return _soldInFlight;
  _soldInFlight = (async () => {
    try {
      const res = await dummyFetch("/api/inventory/?show_sold=true&limit=200", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Failed to load sold inventory");
      const json = await res.json();
      const raw = Array.isArray(json) ? json : json.items || [];
      const sold = raw.filter((item) => item.state === "SOLD" || item.sold);
      _soldCache = { items: sold, ts: Date.now() };
      return sold;
    } finally {
      _soldInFlight = null;
    }
  })();
  return _soldInFlight;
}

export async function fetchBrandBackgrounds() {
  if (_bgsCache.map && isFresh(_bgsCache)) return _bgsCache.map;
  if (_bgsInFlight) return _bgsInFlight;
  _bgsInFlight = (async () => {
    try {
      const res = await fetch("/api/v2/brands/backgrounds");
      if (!res.ok) {
        _bgsCache = { map: {}, hidden: new Set(), ts: failedStamp() };
        return {};
      }
      const brands = await res.json();
      const map = {};
      const hidden = new Set();
      for (const b of brands) {
        const key = String(b.name || "").toLowerCase().trim();
        if (!key) continue;
        if (b.hidden_from_showcase) hidden.add(key);
        if (b.background_url) {
          map[key] = {
            url: b.background_url,
            type: b.background_type || "image",
            darken: b.bg_darken ?? 20,
            vignette: b.bg_vignette ?? 50,
            blur: b.bg_blur ?? 0,
          };
        }
      }
      _bgsCache = { map, hidden, ts: Date.now() };
      return map;
    } catch {
      // Nothing hidden when the request fails: a hiccup must not blank the Showcase.
      _bgsCache = { map: {}, hidden: new Set(), ts: failedStamp() };
      return {};
    } finally {
      _bgsInFlight = null;
    }
  })();
  return _bgsInFlight;
}

const NO_BRANDS = new Set();

// The brands the owner has hidden from the Showcase, as lowercased names. One
// request with the backdrops (fetchBrandBackgrounds), so it shares their cache.
// Empty when that request failed or the API predates the switch, never null.
export async function fetchShowcaseHiddenBrands() {
  await fetchBrandBackgrounds();
  return _bgsCache.hidden || NO_BRANDS;
}

// Synchronous read of the same set: null while it is not known yet (nothing
// fetched, or the cache has expired), so a caller can hold its list back until
// it is rather than show a hidden brand and then take it away.
export function getShowcaseHiddenBrands() {
  return _bgsCache.hidden && isFresh(_bgsCache) ? _bgsCache.hidden : null;
}

export function invalidateInventoryCache() {
  _itemsCache = { items: null, ts: 0 };
  _soldCache = { items: null, ts: 0 };
  _bgsCache = { map: null, hidden: null, ts: 0 };
}

// Synchronous read for components that want to render-without-spinner when
// the cache is already warm (e.g. BrandCarousel's initial useState).
export function getCachedItems() {
  return _itemsCache.items && isFresh(_itemsCache) ? _itemsCache.items : null;
}

// Build the URL list to preload. Order matters: backgrounds first (largest
// visual payoff when /brand opens), then each item's photo 1 (cover) AND
// photo 2 (hover) — the hover image needs to be cached too, otherwise the
// user sees a half-loaded swap on the first hover.
function buildPreloadUrls(items, bgsMap) {
  const bgUrls = Object.values(bgsMap).map((b) => b.url).filter(Boolean);

  const itemUrls = [];
  const slice = items.slice(0, MAX_IMAGES_TO_PRELOAD);
  for (const item of slice) {
    const photos = Array.isArray(item.photos_json) ? item.photos_json : [];
    if (!photos.length) continue;
    const ci = typeof item.cover_index === "number" ? item.cover_index : 0;
    const cover = photos[ci]?.url || photos[0]?.url;
    if (cover) itemUrls.push(cover);
    if (photos.length > 1) {
      const hover = photos[(ci + 1) % photos.length]?.url;
      if (hover) itemUrls.push(hover);
    }
  }

  // Cap total — backgrounds always included; items fill remaining budget.
  const budget = Math.max(0, MAX_IMAGES_TO_PRELOAD - bgUrls.length);
  return [...bgUrls, ...itemUrls.slice(0, budget * 2)]; // *2 because each item contributes up to 2 URLs
}

export async function warmupAll() {
  _warmupAborted = false;
  let items, bgs;
  try {
    [items, bgs] = await Promise.all([fetchItems(), fetchBrandBackgrounds()]);
  } catch {
    return;
  }
  if (_warmupAborted) return;
  // Not on a touchscreen. The trickle below fetches every backdrop and up to
  // 140 product photos (a cover and a hover shot each, about a megabyte
  // apiece) for pages the visitor may never open: on a phone that is data
  // spent, and bandwidth taken from the page on screen. The fetches above
  // still warm the data caches; each page loads its own images as they come
  // on screen, and the showcase queues what it needs next itself.
  if (isTouchDevice()) return;

  const urls = buildPreloadUrls(items || [], bgs || {});
  if (!urls.length) return;

  let i = 0;
  const tick = () => {
    if (_warmupAborted) return;
    for (let j = 0; j < IMAGES_PER_TICK && i < urls.length; j++) {
      // new Image().src triggers an HTTP fetch into the browser cache
      // without ever attaching the element to the DOM. Subsequent <img>
      // tags pointing at the same URL are served from cache.
      new Image().src = urls[i++];
    }
    if (i < urls.length) {
      _warmupTimer = setTimeout(tick, IMAGE_PRELOAD_INTERVAL_MS);
    }
  };
  _warmupTimer = setTimeout(tick, WARMUP_KICKOFF_DELAY_MS);
}

export function cancelWarmup() {
  _warmupAborted = true;
  if (_warmupTimer) {
    clearTimeout(_warmupTimer);
    _warmupTimer = null;
  }
}
